import { useLayoutEffect } from 'react';

import { useMessageWithProcessSpecification } from 'services/repositories/messages/messages';
import { useSenderProcessSpecification } from 'services/repositories/relationships/relationships';
import { setPackingListContext, setPackingListEnabled } from 'stores/packingList/packingList';
import { DocumentType } from 'support/types';

type UseCapabilitiesParams =
  | { messageId: string | undefined }
  | { relationshipId: string | undefined; sourceMessageId: string | undefined; documentType: DocumentType | undefined };

/**
 * Syncs the backend packing-list capability into the packingList store, along with the document
 * context (relationship + document type) that the SSCC-generation endpoint resolves against. View,
 * edit and reprocess read the capability off the `/v2/messages` envelope (a sibling of `data`) and
 * derive the context from the same message; the create flow reads the capability off the sender
 * process spec (the receiver spec that drives creation does not carry capabilities) and takes the
 * document type explicitly.
 *
 * Both underlying queries are already in flight at the call sites (deduped by key), so this issues
 * no extra request; the unused branch is disabled via its own `enabled` guard by passing undefined
 * variables.
 */
export const useCapabilities = (params: UseCapabilitiesParams) => {
  const messageId = 'messageId' in params ? params.messageId : undefined;
  const relationshipId = 'relationshipId' in params ? params.relationshipId : undefined;
  const sourceMessageId = 'sourceMessageId' in params ? params.sourceMessageId : undefined;
  const createDocumentType = 'documentType' in params ? params.documentType : undefined;

  const { data: message } = useMessageWithProcessSpecification({ variables: { messageId } });
  const { data: senderProcessSpecification } = useSenderProcessSpecification({
    variables: { relationshipId, sourceMessageId },
  });

  const capabilities = messageId ? message?.capabilities : senderProcessSpecification?.capabilities;
  const packingListEnabled = capabilities?.packing_list?.enabled ?? false;

  const contextRelationshipId = messageId ? message?.data?.relationship_id : relationshipId;
  const contextDocumentType = messageId ? (message?.data?.type as DocumentType | undefined) : createDocumentType;

  // Child forms validate in passive effects on mount. Synchronize first so their resolvers read the
  // capability that produced the current document instead of the store's reset default.
  useLayoutEffect(() => {
    setPackingListEnabled(packingListEnabled);
    setPackingListContext({ relationshipId: contextRelationshipId, documentType: contextDocumentType });
  }, [packingListEnabled, contextRelationshipId, contextDocumentType]);
};
